/**
 * <lookbook-tabs> — async page-swap tab bar for lookbook pages.
 *
 * Each tab is a real link to a lookbook page (lookbook-summer / -winter / -editorial),
 * and each page works standalone. On click (outside the editor) we fetch the target
 * page and swap the whole #MainContent innerHTML — EVERYTHING under the global top bar
 * changes (hero + tabs + grid + the sections below it) — wrapped in a View Transition
 * for a seamless crossfade. The top bar / announcement bar live outside #MainContent so
 * they persist. Without JS the links are a normal full page load (fallback).
 *
 * The swapped-in <lookbook-tabs> re-upgrades itself (this module is global), and the new
 * tabs already carry the correct `is-active` / aria-current from the fetched page's
 * `link.active`, so there's nothing to sync by hand.
 *
 * Ships with sections/section-lookbook-looks.liquid.
 *
 * Dependencies: @theme/delegate, @theme/popup
 */

import { Delegate } from '@theme/delegate';
import { globalPopup } from '@theme/popup';

// Module scope so the page cache + listeners survive swaps and multiple instances.
const pageCache = new Map();
const MAIN_ID = 'MainContent';

class LookbookTabs extends HTMLElement {
  #navAbort = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.domDelegate = new Delegate(this);
    this.domDelegate.on('click', 'a[href]', this.#onTabClick);
    this.domDelegate.on('mouseover', 'a[href]', this.#onTabHover);

    // delegate-exempt: popstate is a window-only event (can't be delegated off `this`).
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    this.domDelegate?.off();
    window.removeEventListener('popstate', this.#onPopState);
    this.#navAbort?.abort(); // drop any in-flight fetch
  }

  // --- Tab navigation (async #MainContent swap). ---

  #onTabClick = (event, target) => {
    // In the theme editor let the normal link work (each page is real).
    if (window.Shopify && window.Shopify.designMode) return;
    // Respect new-tab / modifier clicks.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (target.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.#navigate(target.href, true);
  };

  #onTabHover = (event, target) => {
    if (window.Shopify && window.Shopify.designMode) return;
    this.#prefetch(target.href);
  };

  #onPopState = () => {
    this.#navigate(window.location.href, false);
  };

  async #prefetch(url) {
    if (!url || pageCache.has(url)) return;
    try {
      await this.#fetchPage(url);
    } catch (e) {
      // Ignore prefetch errors; the click retries / falls back.
    }
  }

  async #fetchPage(url, signal) {
    if (pageCache.has(url)) return pageCache.get(url);
    const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' }, signal });
    // fetch only rejects on network failure — a 404/500 still resolves. Don't cache or
    // swap in an error page; throw so #navigate falls back to a hard navigate.
    if (!response.ok) throw new Error(`Lookbook fetch ${response.status} for ${url}`);
    const html = await response.text();
    pageCache.set(url, html);
    return html;
  }

  async #navigate(url, push) {
    // Abort a previous in-flight navigation so a slow earlier fetch can't overwrite
    // the page after a newer tab click (stale-overwrite race).
    this.#navAbort?.abort();
    this.#navAbort = new AbortController();

    let doc;
    try {
      const html = await this.#fetchPage(url, this.#navAbort.signal);
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      if (e.name === 'AbortError') return; // superseded by a newer navigation / disconnect
      window.location.href = url; // network failure → hard navigate
      return;
    }

    const newMain = doc.getElementById(MAIN_ID);
    const main = document.getElementById(MAIN_ID);
    if (!newMain || !main) {
      window.location.href = url; // structure not as expected → hard navigate
      return;
    }

    // The fetched document carries its own page context; analytics would otherwise keep
    // reporting the first page's type/template/handle after the swap.
    const pageContext = doc.getElementById('ThemePageContext');
    if (pageContext && window.theme) {
      try {
        window.theme.page = JSON.parse(pageContext.textContent);
      } catch {
        // keep the previous context
      }
    }

    // Close any open shop-the-look popup before the swap (its DOM is replaced; this
    // also clears the body scroll lock + overlay cleanly, before the VT snapshot).
    document
      .querySelectorAll('.stl-popup[aria-hidden="false"]')
      .forEach((popup) => globalPopup.closePopup(popup));

    // Swap EVERYTHING under the top bar (#MainContent). Inline <style> in the new HTML
    // applies (unlike <script>), and the new Custom Elements auto-upgrade (their modules
    // are already loaded). innerHTML source is our own same-origin theme HTML (a fetched
    // lookbook page), not user input — same trusted-source pattern as collection-filters.
    const swap = () => {
      main.innerHTML = newMain.innerHTML;
    };
    if (document.startViewTransition) {
      document.startViewTransition(swap);
    } else {
      swap();
    }

    const newTitle = doc.querySelector('title')?.textContent;
    if (newTitle) document.title = newTitle;
    if (push) history.pushState({ lookbook: true }, '', url);
  }
}

if (!customElements.get('lookbook-tabs')) {
  customElements.define('lookbook-tabs', LookbookTabs);
}
